import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usersApi, type CreateUserInput } from '../api/users'
import type { Role, User } from '../lib/types'
import { ApiError } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { Spinner } from '../components/Spinner'

export default function Users() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() })
  const users = data?.users ?? []

  const [showCreate, setShowCreate] = useState(false)

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Usuários</h1>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
        >
          {showCreate ? 'Fechar' : '+ Novo usuário'}
        </button>
      </div>

      {showCreate && <CreateUserForm onDone={() => setShowCreate(false)} />}

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Nome</th>
                <th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">Papel</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <UserRow key={u.id} user={u} onChanged={() => qc.invalidateQueries({ queryKey: ['users'] })} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function CreateUserForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState<CreateUserInput>({
    name: '',
    email: '',
    password: '',
    role: 'manager',
  })
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => usersApi.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      onDone()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Erro ao criar usuário'),
  })

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-3 text-base font-semibold text-slate-900">Novo usuário</h2>
      {error && <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          placeholder="Nome"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className={inputCls}
        />
        <input
          placeholder="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          className={inputCls}
        />
        <input
          placeholder="Senha (mín. 8 caracteres)"
          type="text"
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          className={inputCls}
        />
        <select
          value={form.role}
          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}
          className={inputCls}
        >
          <option value="manager">Gerente</option>
          <option value="admin">Administrador</option>
        </select>
      </div>
      <button
        onClick={() => {
          setError(null)
          create.mutate()
        }}
        disabled={create.isPending}
        className="mt-3 inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
      >
        {create.isPending && <Spinner className="h-4 w-4 text-white" />}
        Criar
      </button>
    </div>
  )
}

function UserRow({ user, onChanged }: { user: User; onChanged: () => void }) {
  const { user: currentUser } = useAuth()
  const isSelf = currentUser?.id === user.id
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(user.name)
  const [email, setEmail] = useState(user.email)
  const [role, setRole] = useState<Role>(user.role)
  const [active, setActive] = useState(user.active ?? true)

  const update = useMutation({
    mutationFn: () => usersApi.update(user.id, { name, email, role, active }),
    onSuccess: () => {
      setEditing(false)
      onChanged()
    },
  })
  const resetPwd = useMutation({
    mutationFn: (password: string) => usersApi.resetPassword(user.id, password),
  })
  const remove = useMutation({
    mutationFn: () => usersApi.remove(user.id),
    onSuccess: onChanged,
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Não foi possível apagar o usuário'),
  })

  if (editing) {
    return (
      <tr className="bg-slate-50">
        <td className="px-4 py-2">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        </td>
        <td className="px-4 py-2">
          <input value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        </td>
        <td className="px-4 py-2">
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} className={inputCls}>
            <option value="manager">Gerente</option>
            <option value="admin">Administrador</option>
          </select>
        </td>
        <td className="px-4 py-2">
          <label className="inline-flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Ativo
          </label>
        </td>
        <td className="px-4 py-2 text-right">
          <button
            onClick={() => update.mutate()}
            disabled={update.isPending}
            className="mr-2 text-sm font-medium text-brand-600 hover:underline"
          >
            Salvar
          </button>
          <button onClick={() => setEditing(false)} className="text-sm text-slate-500 hover:underline">
            Cancelar
          </button>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td className="px-4 py-2.5 font-medium text-slate-900">{user.name}</td>
      <td className="px-4 py-2.5 text-slate-600">{user.email}</td>
      <td className="px-4 py-2.5 text-slate-600">
        {user.role === 'admin' ? 'Administrador' : 'Gerente'}
      </td>
      <td className="px-4 py-2.5">
        {user.active ?? true ? (
          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
            Ativo
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
            Inativo
          </span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right">
        <button onClick={() => setEditing(true)} className="mr-3 text-sm text-brand-600 hover:underline">
          Editar
        </button>
        <button
          onClick={() => {
            const pwd = prompt(`Nova senha para ${user.name} (mín. 8 caracteres):`)
            if (pwd) resetPwd.mutate(pwd)
          }}
          className="mr-3 text-sm text-slate-500 hover:underline"
        >
          {resetPwd.isPending ? 'Redefinindo…' : 'Redefinir senha'}
        </button>
        {!isSelf && (
          <button
            onClick={() => {
              if (confirm(`Apagar o usuário "${user.name}"? Esta ação não pode ser desfeita.`))
                remove.mutate()
            }}
            disabled={remove.isPending}
            className="text-sm text-red-600 hover:underline disabled:opacity-50"
          >
            {remove.isPending ? 'Apagando…' : 'Apagar'}
          </button>
        )}
      </td>
    </tr>
  )
}

const inputCls =
  'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none'
